"use client"

import axios from "axios"
import React, { useEffect, useState, useContext, useMemo } from "react"
import { Link } from "react-router-dom"
import { CircuitoContext } from "../../context/CircuitoContext"
import { FaHome, FaNewspaper, FaClock, FaChevronRight } from "react-icons/fa"
import "../../styles/circuitoHomeV2.css"
import { DomainContext } from "./LayoutCircuito"

const API = "https://api.ticketdovaqueiro.com"

const CATEGORIAS = [
  { key: "todas",      label: "Todas"       },
  { key: "destaque",   label: "Destaque"    },
  { key: "vaquejadas", label: "Vaquejadas"  },
  { key: "campeonato", label: "Campeonatos" },
  { key: "ranking",    label: "Ranking"     },
  { key: "avisos",     label: "Avisos"      },
]

function formatDate(dateString) {
  if (!dateString) return ""
  return new Date(dateString).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "short", year: "numeric",
  }).toUpperCase().replace(/\./g, "")
}

function timeAgo(dateString) {
  if (!dateString) return "Agora"
  const diff = Date.now() - new Date(dateString).getTime()
  const min = Math.floor(diff / 60000)
  const h   = Math.floor(min / 60)
  const d   = Math.floor(h / 24)
  if (d >= 30) return `${Math.floor(d / 30)} mês(es) atrás`
  if (d >= 1)  return `${d} dia(s) atrás`
  if (h >= 1)  return `${h} hora(s) atrás`
  if (min >= 1) return `${min} min atrás`
  return "Agora"
}

export default function CircuitoNoticiasLista() {
  const { circuito }        = useContext(CircuitoContext)
  const { basePath }        = useContext(DomainContext)
  const [noticias, setNoticias] = useState([])
  const [loading, setLoading]   = useState(true)
  const [categoriaAtiva, setCategoriaAtiva] = useState("todas")

  useEffect(() => {
    const carregar = async () => {
      try {
        if (!circuito?._id) return
        setLoading(true)
        const res = await axios.get(`${API}/api/circuitos/${circuito._id}/noticias`)
        setNoticias(res.data || [])
      } catch (e) {
        console.error(e); setNoticias([])
      } finally {
        setLoading(false)
      }
    }
    carregar()
  }, [circuito?._id])

  const noticiasFiltradas = useMemo(() => {
    if (categoriaAtiva === "todas") return noticias
    return noticias.filter(n => (n.categoria || "geral") === categoriaAtiva)
  }, [noticias, categoriaAtiva])

  const [destaque, ...restantes] = noticiasFiltradas

  return (
    <div className="ch-page">
      <div className="ch-container" style={{ paddingTop: 32, paddingBottom: 64 }}>

        {/* Breadcrumb */}
        <nav className="ch-breadcrumb">
          <FaHome size={12} />
          <Link to={`${basePath}/`}>Início</Link>
          <span>/</span>
          <span>Notícias</span>
        </nav>

        {/* Header */}
        <div className="ch-page-header">
          <h1 style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <FaNewspaper style={{ color: "var(--c-accent, #d4af37)", flexShrink: 0 }} />
            Notícias
          </h1>
          <div className="ch-page-header__divider" />
        </div>

        {/* Filtro de categorias */}
        <div className="ch-cat-pills">
          {CATEGORIAS.map(c => (
            <button
              key={c.key}
              className={`ch-cat-pill ${categoriaAtiva === c.key ? "ch-cat-pill--active" : ""}`}
              onClick={() => setCategoriaAtiva(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="ch-loading">Carregando notícias...</div>
        ) : noticiasFiltradas.length === 0 ? (
          <div className="ch-empty">Nenhuma notícia publicada nessa categoria.</div>
        ) : (
          <>
            {/* Card destaque (primeiro) */}
            {destaque && (
              <Link
                to={`${basePath}/noticias/${destaque._id}`}
                className="ch-news-featured-card"
              >
                <div className="ch-news-featured-card__img-wrap">
                  <img
                    src={destaque.imagem
                      ? `${API}/uploads/${destaque.imagem}`
                      : "https://via.placeholder.com/900x500?text=Notícia"
                    }
                    alt={destaque.titulo}
                    className="ch-news-featured-card__img"
                  />
                </div>

                <div className="ch-news-featured-card__body">
                  <div className="ch-news-featured-card__badge">
                    <span className="ch-date-badge">
                      <FaClock size={9} />
                      {formatDate(destaque.createdAt)}
                    </span>
                    {destaque.categoria && destaque.categoria !== "geral" && (
                      <span className="ch-cat-badge">{destaque.categoria}</span>
                    )}
                  </div>

                  <h2 className="ch-news-featured-card__title">{destaque.titulo}</h2>

                  {destaque.subtitulo && (
                    <p className="ch-news-featured-card__subtitle">{destaque.subtitulo}</p>
                  )}

                  <span className="ch-news-featured-card__read">
                    Ler matéria <FaChevronRight size={10} />
                  </span>
                </div>
              </Link>
            )}

            {/* Grid das demais notícias */}
            {restantes.length > 0 && (
              <div className="ch-news-cards-grid">
                {restantes.map(n => (
                  <Link
                    key={n._id}
                    to={`${basePath}/noticias/${n._id}`}
                    className="ch-news-card-v2"
                  >
                    <div className="ch-news-card-v2__img-wrap">
                      <img
                        src={n.imagem
                          ? `${API}/uploads/${n.imagem}`
                          : "https://via.placeholder.com/600x360?text=Notícia"
                        }
                        alt={n.titulo}
                        className="ch-news-card-v2__img"
                      />
                      {n.categoria && n.categoria !== "geral" && (
                        <div className="ch-news-card-v2__badge-wrap">
                          <span className="ch-cat-badge">{n.categoria}</span>
                        </div>
                      )}
                    </div>

                    <div className="ch-news-card-v2__body">
                      <p className="ch-news-card-v2__title">{n.titulo}</p>
                      {n.subtitulo && (
                        <p className="ch-news-card-v2__subtitle">{n.subtitulo}</p>
                      )}
                      <div className="ch-news-card-v2__footer">
                        <span className="ch-news-card-v2__date">{formatDate(n.createdAt)}</span>
                        <FaChevronRight size={10} style={{ color: "rgba(255,255,255,0.25)" }} />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}